import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { WelcomeStep } from "./WelcomeStep/WelcomeStep";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	UserCultureResult,
	getUserCulture,
	setUserCulture
} from "client/Api/Localization/userCulture";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	BusyIndicator,
	Form,
	FormItem,
	ValidationError
} from "client/Components";
import { ApplicationCultureSelect } from "../Users/Culture/ApplicationCultureSelect";
import image from "Content/Images/language-step.svg";
import { ContentLayout } from "./ContentLayout";

export type LanguageStepProps = {
	onNext: (culture: string) => void;
};

export const LanguageStep = (props: LanguageStepProps): ReactElement => {
	const {
		onNext: onNextFromProps
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [selectedCulture, setSelectedCulture] = useState<string>(() => getCurrentCulture());

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<string> = useFormHandler<string>(
		selectedCulture,
		setSelectedCulture,
		validationConfiguration
	);

	const onNext: (culture: string) => void = useStableImperativeCallback(onNextFromProps);

	useEffect(
		(): (() => void) => {
			setIsBusy(true);
			async function getCulture(signal: AbortSignal): Promise<void> {
				try {
					const result: UserCultureResult = await getUserCulture(signal);

					if (result.userCultureDetermined) {
						setSelectedCulture(result.userCulture);
					}

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation: AbortController = new AbortController();
			getCulture(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				await setUserCulture(selectedCulture, globalCancellation);

				setIsBusy(false);
				onNext(selectedCulture);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
				setIsBusy(false);
			}
		},
		[
			alertManager,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onNext,
			selectedCulture
		]
	);

	return (
		<>
			<WelcomeStep.Left trackingId="language-step">
				{isBusy && (
					<BusyIndicator />
				)}

				<ContentLayout
					title={localize("_LanguageStepTitle")}
					explanation={localize("_LanguageStepExplanation")}
				>
					<Form onSubmit={saveHandler}>
						<FormItem label={localize("_LanguagePreference")}>
							<ApplicationCultureSelect
								{...formHandler.attachToSelect()}
								trackingId="culture"
								menuTitle={localize("_Language")}
							/>

							<ValidationError {...formHandler.attachToValidationError()} />
						</FormItem>
					</Form>

					<ContentLayout.Buttons
						onContinue={saveHandler}
					/>
				</ContentLayout>
			</WelcomeStep.Left>

			<WelcomeStep.Right
				imageUrl={image}
				text={localize("_UnlockingYourNewDigitalWorkspaceFocus")}
			/>
		</>
	);
};
LanguageStep.displayName = "LanguageStep";

const validationConfiguration: IValidationConfiguration<string> = createValidationConfiguration<string>();

validationConfiguration.add(
	isNotNullNorUndefined(),
	isNotEmpty()
);
